import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Orientation } from '../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import type { CustomError } from '../../../_ui-core_/base/package/patterns/package/CError';
import { I18n } from '../../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { sleep } from '../../../_ui-core_/base/package/util/render';
import { UIGenericErrorModal } from '../../../_ui-core_/components/drawers/generic-error-modal/ui-generic-error-modal';
import type { ContributionTarget } from '../../../_ui-core_/mod-incentive/types';
import App from '../../../app';
import { UserStates } from '../../../app/package/base/service/activation-flow/activation-flow-domain';
import { ModalType } from '../../../app/package/base/service/modals/modal-service';
import type { UnsubscribeFunc } from '../../../app/package/base/service/native/native-bridge';
import { getGameType, TrackableEventAction } from '../../../app/package/base/service/tracking/tracking-service';
import { createGameController } from '../../../components/controller/game/_factory';
import type { IGameController } from '../../../components/controller/game/base-controller';
import style from './ui-game-view.css?inline';
import '../../../_ui-core_/components/ui-progress-bar/ui-progress-bar';
import '../../../_ui-core_/components/ui-spinner/ui-spinner';

const CName = 'ui-game-view';

export const gameView: { el?: UIGameView } = {};

@customElement(CName)
export class UIGameView extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Boolean }) forReal: boolean;
  @property({ type: String }) gameId: string;
  @property({ type: String }) gameName: string;
  @property({ type: String }) gameSlug: string;
  @property({ type: Boolean }) isNativeIos = false;
  @property({ type: Object }) incentiveMission: ContributionTarget | null;

  @query('iframe') private readonly _iframeEl?: HTMLIFrameElement;

  @state() private _iframeUrl: string | undefined;
  @state() private _portraitMode = false;
  @state() private _messageModalVisible = false;
  @state() private _modal = {
    title: '',
    message: '',
  };

  private _newBalance = 0;
  private _oldBalance = 0;
  private _stake?: string = undefined;

  private _activeGameForReal: boolean;
  private _activeGameId: string;
  private _gameController: IGameController;
  private _isNewGameId = true;
  private _launchAbortController: AbortController | null = null;
  private _shouldUseMigratedGameId = true;
  private _wasFirstSpinDone = false;
  private _wasTrackingErrorSent = false;
  private readonly _spinCounter = {
    _count: 0,
    increment() {
      this._count++;
    },
    reset() {
      this._count = 0;
    },
    get count() {
      return this._count;
    },
  };

  // iOs native related
  @state() private readonly _gameLoadProgress = 0;
  private readonly _gameLoadProgressUnsubscribeFn: UnsubscribeFunc;
  private readonly _gameStartCompleteUnsubscribeFn: UnsubscribeFunc;

  /**
   * Delay in ms between iframe teardown and new iframe insertion.
   * Gives WebKit's GC time to reclaim memory from the previous game context,
   * preventing process termination under memory pressure (IGM-3644).
   */
  private static readonly IFRAME_TEARDOWN_DELAY_MS = 500;

  // This needs to be defined via a lambda, as we want to use it as a listener callback, that we can remove afterward
  private readonly _forwardMessageEvent = (message: MessageEvent<any>) => {
    let data: string | object = message.data;
    try {
      data = JSON.parse(message.data);
    } catch (err) {
      window.$app.logger.log('parsing failed:', err);
      // ignore and keep string
    }
    const result = this._gameController.handleMessageEvent(data);

    window.$app.logger.log('message data:', data, '• result:', result);
    if (result !== null) {
      switch (result.action) {
        case 'markActivityAndSpinSuccess':
          App.instance.markActivity();
          this._spinCounter.increment();
          if (!this._wasFirstSpinDone) {
            this._trackGamePlay(
              App.instance.appSettings.isSHCasino
                ? TrackableEventAction.FIRST_ROUND_COMPLETE
                : TrackableEventAction.FIRST_SPIN_SUCCESS,
            );
            this._wasFirstSpinDone = true;
          }
          break;
        case 'reload':
          App.instance.router.reloadPage();
          break;
        case 'reloadGame':
          this.reloadGame();
          break;
        case 'redirect': {
          // Hide iframe immediately to prevent error flash during teardown (IGM-3820)
          if (this._iframeEl) {
            this._iframeEl.style.visibility = 'hidden';
          }
          const spins = this._spinCounter.count;
          this._trackGameEnded(this.gameId, spins);
          App.instance.router.navigateTo(result.url ?? document.location.origin);
          break;
        }
        case 'markActivity':
          App.instance.markActivity();
          break;
        case 'balanceUpdated': {
          const incomingBalance = Number(result.balance);

          if (this._newBalance === 0 && this._oldBalance === 0) {
            this._newBalance = incomingBalance;
            this._oldBalance = incomingBalance;
            return;
          }

          this._oldBalance = this._newBalance;
          this._newBalance = incomingBalance;

          const diff = this._oldBalance - this._newBalance;

          if (diff > 0) {
            this._stake = diff.toFixed(2);
          } else {
            this._stake = undefined;
          }

          break;
        }
        case 'error':
          if (!this._wasTrackingErrorSent) {
            this._trackGamePlay(TrackableEventAction.IN_GAME_ERROR, result.error);
            this._wasTrackingErrorSent = true;
          }
          // Ignore: https://tipico-us.atlassian.net/browse/IGM-262
          break;
      }
    }
  };

  public async reloadGame() {
    this._iframeUrl = undefined;
    this.requestUpdate();
    await this.updateComplete;
    this._launchGame(this.gameId);
  }

  connectedCallback() {
    super.connectedCallback();
    gameView.el = this;
    document.body.classList.add('no-scroll-game-view');
    this._getCasinoConfig();
    App.instance.content.getGame(this.gameId).then((game) => {
      if (!this.isConnected) return;
      this._portraitMode = game?.exclude?.orientations?.includes(Orientation.LANDSCAPE) ?? false;
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    gameView.el = undefined;
    document.body.classList.remove('no-scroll-game-view');
    this._removePostMessageListener();
    this._gameController?.destroy();
    // Abort any in-flight game launch to prevent orphaned iframes
    this._launchAbortController?.abort();
    this._launchAbortController = null;
    const { gameSource } = App.instance.trackingStore.value;
    App.instance.trackingStore.next({ gameSource });
    if (this.isNativeIos) {
      this._gameLoadProgressUnsubscribeFn?.();
      this._gameStartCompleteUnsubscribeFn?.();
      App.instance.native.gameStop();
    }
    window.$app.vaix.updateVaixData({});
    const spins = this._spinCounter.count;
    this._trackGameEnded(this.gameId, spins);
  }

  updated() {
    if (this.gameId !== this._activeGameId || this.forReal !== this._activeGameForReal) {
      if (this._activeGameId) {
        const spins = this._spinCounter.count;
        this._trackGameEnded(this._activeGameId, spins);
      }
      this._gameController?.destroy();
      if (this.gameId !== undefined) {
        this._wasFirstSpinDone = false;
        this._isNewGameId = true;
        this._spinCounter.reset();
        this._launchGame(this.gameId);
        window.$app.vaix.trackGameClickVaix();
      }
    }
  }

  render() {
    return html`
      ${this.renderGame()} ${this._messageModalVisible ? this._renderMessageModal() : nothing}
    `;
  }

  private _renderMessageModal() {
    return html`
      <ui-modal
        class="modal--centered"
        .onClosedAction=${() => {
          this._messageModalVisible = false;
          App.instance.router.navigateBack();
        }}
      >
        <div slot="icon">
          <ui-attention-icon name="error" class="size-xl"></ui-attention-icon>
        </div>
        <div slot="title">${this._modal.title}</div>
        <div slot="main">${this._modal.message}</div>
      </ui-modal>
    `;
  }

  private _renderFrame() {
    if (this._iframeUrl !== undefined) {
      const iframeClass = this._portraitMode ? 'portrait-mode' : '';
      // Start hidden to prevent white flash while the game loads (IGM-3644)
      return html`
        <iframe
          title=${this.gameName}
          @load=${this._handleIframeLoaded}
          class=${iframeClass}
          style="visibility:hidden"
          sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-presentation allow-scripts allow-same-origin allow-top-navigation"
          src=${this._iframeUrl}
        ></iframe>
      `;
    }
    const progress = Math.min(100, this._gameLoadProgress * 100);
    return this.isNativeIos
      ? html`<ui-progress-bar class="progress-bar" .progress="${progress}"></ui-progress-bar>`
      : html`<ui-spinner></ui-spinner>`;
  }

  private renderGame() {
    return html`
      <div class="game-view">
        <div class="iframe-wrapper">${this._renderFrame()}</div>
      </div>
    `;
  }

  private async _handleLaunchError(err: CustomError<any>) {
    this._trackGamePlay(TrackableEventAction.GAME_OPEN_FAIL);
    window.$app.logger.warn('Error during game launch:', err.code, err.message, err.type);
    if (err.code === 403 && err.type?.endsWith(':user-activation-failed')) {
      await App.instance.activationFlow.checkUserActivation();
      App.instance.router.navigateToHome();

      // show error modal on successful activation to explain redirect (super edge case)
      if (App.instance.activationFlow.getCurrentState() === UserStates.ACTIVATED) {
        this._showErrorModal();
      }
      return;
    }

    this._messageModalVisible = true;
    this._modal = {
      title: window.$app.$t.get(I18n.popup.gameLaunch.error),
      message: window.$app.$t.get(I18n.popup.gameLaunch.failed),
    };
  }

  private _showErrorModal() {
    const modal = new UIGenericErrorModal();
    window.$app.modal.show(modal, ModalType.GenericError);
  }

  private async _launchGame(gameId: string, gameBaseUrl?: string) {
    // Abort any previous in-flight launch (rapid game switching via discovery modal)
    this._launchAbortController?.abort();
    const controller = new AbortController();
    this._launchAbortController = controller;

    this._activeGameId = gameId;
    this._activeGameForReal = this.forReal;
    const migratedGameId = this._shouldUseMigratedGameId ? await this._getMigratedGameId() : gameId;
    if (controller.signal.aborted) return;

    App.instance.playedGames.set(migratedGameId);
    const [integration, integrationGameId] = migratedGameId.split(':');

    if (!integration || !integrationGameId) {
      window.$app.logger.warn('Unknown integration or integrationGameId. Can not launch game.', integration);
      return;
    }
    this._gameController = createGameController(this, integration);
    window.$app.logger.log('launch game:', migratedGameId);
    if (gameBaseUrl) {
      this._gameController
        .getLaunchUrlIOs(this.forReal, integrationGameId, gameBaseUrl)
        .then((url) => {
          if (controller.signal.aborted) return;
          this._updateIframe(url, controller);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          this._handleLaunchError(err);
        });
      return;
    }

    this._gameController
      .getLaunchUrl(this.forReal, integrationGameId)
      .then((url) => {
        if (controller.signal.aborted) return;
        this._updateIframe(url, controller);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        this._handleLaunchError(err);
      });
  }

  private _handleIframeLoaded() {
    if (!this._iframeEl || !this._iframeUrl) {
      window.$app.logger.warn('Iframe element or url is not available');
      return;
    }

    // Reveal iframe now that the game content has loaded (IGM-3644)
    this._iframeEl.style.visibility = 'visible';

    // This is used to setup non-standard event listeners for
    // certain providers (like playngo)
    this._gameController.handleAdditionalEventListeners(this._iframeUrl, this._iframeEl);
    window.addEventListener('message', this._forwardMessageEvent);
    if (this._isNewGameId) {
      this._trackGamePlay(TrackableEventAction.GAME_OPEN_SUCCESS);
      this._isNewGameId = false;
    }
  }

  private _removePostMessageListener() {
    window.removeEventListener('message', this._forwardMessageEvent);
  }

  private async _getMigratedGameId() {
    const res = await App.instance.content.migrateGameId(this.gameId);
    if (res) {
      window.$app.logger.log(`Migrating gameId from ${this.gameId} to ${res.gameId}`);
      return res.gameId;
    }

    window.$app.logger.error(`Migrating gameId for ${this.gameId} failed with response:`, res);
    return '';
  }

  private async _updateIframe(url: string, controller: AbortController) {
    // Tear down existing iframe explicitly to release memory (IGM-3644).
    // Setting src to about:blank forces WebKit to discard the browsing context
    // (WebGL, audio, workers) before we remove the element from the DOM.
    // We pause after each step to let WebKit commit the navigation and GC.
    if (this._iframeUrl !== undefined) {
      window.$app.logger.log('cleanup existing iframe…');
      this._removePostMessageListener();
      if (this._iframeEl) {
        // Hide iframe before navigating to about:blank to prevent white flash
        this._iframeEl.style.visibility = 'hidden';
        this._iframeEl.src = 'about:blank';
      }
      await sleep(UIGameView.IFRAME_TEARDOWN_DELAY_MS);
      if (controller.signal.aborted) return;
      this._iframeUrl = undefined;
      await sleep(UIGameView.IFRAME_TEARDOWN_DELAY_MS);
      if (controller.signal.aborted) return;
    } else {
      // First game load — wait for the GPU process to release lobby image buffers
      // that were cleared by _releaseImageMemory() in the router (IGM-3644).
      await sleep(UIGameView.IFRAME_TEARDOWN_DELAY_MS);
      if (controller.signal.aborted) return;
    }
    window.$app.logger.log('update iframe:', url);
    this._iframeUrl = url;
  }

  private _trackGamePlay(eventAction: TrackableEventAction, error?: string) {
    const isGameOpenFailed = eventAction === TrackableEventAction.GAME_OPEN_FAIL;
    const errorMessage = isGameOpenFailed ? window.$app.$t.get(I18n.popup.gameLaunch.failed) : error;
    App.instance.content
      .getGameInfo(this.gameId)
      .then(async (gameInfo) => {
        const game = await App.instance.content.getGame(this.gameId);
        const gameType = getGameType(game.title);
        const stakeAmount = App.instance.appSettings.isSHCasino && this._stake ? this._stake : undefined;
        if (game && gameInfo) {
          window.$app.track.gamePlay({
            eventAction,
            gameCategory: game.labels?.join(' - '),
            gameFilters: App.instance.trackingStore.value.gameFilters,
            gameName: game.title,
            gamePosition: App.instance.trackingStore.value.gamePosition,
            gameProvider: gameInfo.getStudio(),
            gameSource: App.instance.trackingStore.value.gameSource,
            winType: App.instance.trackingStore.value.winType,
            gameType,
            ...(errorMessage ? { errorMessage } : {}),
            ...(stakeAmount ? { stakeAmount } : {}),
            ...(errorMessage ? { errorSource: isGameOpenFailed ? 'internal' : 'external' } : {}),
            ...(this.incentiveMission?.bundleId ? { gameMission: this.incentiveMission?.bundleId } : {}),
            ...(this.incentiveMission?.reward.bonusId ? { gameReward: this.incentiveMission?.reward.bonusId } : {}),
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info for tracking '${this.gameId}'`, err);
      });
  }

  private _trackGameEnded(gameId: string = this.gameId, spinCount: number = this._spinCounter.count) {
    App.instance.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        const game = await App.instance.content.getGame(gameId);
        const gameType = getGameType(game.title);
        const stakeAmount = App.instance.appSettings.isSHCasino && this._stake ? this._stake : undefined;
        if (game && gameInfo) {
          window.$app.track.gameEnded({
            eventAction: App.instance.appSettings.isSHCasino
              ? TrackableEventAction.GAME_ENDED_SH
              : TrackableEventAction.GAME_ENDED,
            spinCount: String(spinCount),
            gameName: game.title,
            gameProvider: gameInfo.getStudio(),
            gameType,
            ...(stakeAmount ? { stakeAmount } : {}),
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info for tracking '${gameId}'`, err);
      });
  }

  private _getCasinoConfig() {
    const shouldUseMigratedGameId = window.$app.casinoConfig.migrateIdOnGameLaunchEnabled;
    if (shouldUseMigratedGameId !== undefined) {
      this._shouldUseMigratedGameId = shouldUseMigratedGameId;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIGameView;
  }
}
